<!-- Popup: TOC(Table of Contents) -->
<script lang="ts" setup>
import { computed, inject, nextTick, ref, unref, watch } from 'vue'
import { useScroll } from '@vueuse/core'
import { api } from '@/api'
import { useLoadingStore } from '@/stores'
import { CHAPTER_LOCK } from '@/constants'
import { useElementClick } from '@/composables/tracks'
import { useI18n } from '@/composables/useI18n'
import { formatUpdateTime } from '@/utils'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { theme as brandTheme } from '@/config/brands'

const props = withDefaults(
  defineProps<{
    value: boolean
    book: any
    contentId: string
    chapterId?: string
    checkPassGotoChapter?: (index: number) => boolean
    lockIconColor?: string
    routeName?: string
  }>(), {
    checkPassGotoChapter: () => true,
    routeName: READER_ROUTE_NAME,
  },
)

defineEmits<{
  (event: 'input', value: boolean): void
}>()

const LONG_BOOK_CHAPTER_THRESHOLD = 25

const theme = inject<any>('theme', { background: 'white' })
const { $showLoading, $hideLoading } = useLoadingStore()
const { elementClick } = useElementClick()
const { t } = useI18n()

const chapterList = ref<GetContentGetcontentchapterbooklistResponse['data']['records']>([])
const chapterScrollRef = ref()
const { y: chapterScrollY } = useScroll(chapterScrollRef, { behavior: 'smooth' })
const chooseChapterOpitons = ref(false)
const chapterOptions = ref<any[]>([])
const chapterTotal = ref<number>(0)
const pagination = ref({
  pageIndex: 1,
  pageSize: 100,
})

const themeBackground = computed(() => unref(theme)?.background || 'white')
const isLightTheme = computed(() => themeBackground.value.toUpperCase() !== '#292929')
const currentChapterSequenceNo = computed(() => {
  if (!props.chapterId)
    return null
  return chapterList.value.find(item => item.id === props.chapterId)?.sequenceNo ?? null
})
const popupBackground = computed(() => isLightTheme.value ? '#FFFFFF' : themeBackground.value)
const tocCurrentColor = computed(() => brandTheme.primaryColor || '#ff6b3d')
const tocPanelStyle = computed(() => {
  if (isLightTheme.value)
    return '--flipnovel-toc-bg:#FFFFFF;--toc-border-color:#F3F4F6;--toc-lock-color:#A8AFBB;'
  return `--flipnovel-toc-bg:${themeBackground.value || '#292929'};--toc-lock-color:#FFFFFF;`
})

const showUpdateTime = computed(() => chapterTotal.value > LONG_BOOK_CHAPTER_THRESHOLD)

function getChapterTextClass(item: any) {
  const isCurrent = props.chapterId === item.id
  if (isCurrent)
    return { 'toc-text--current': true }

  if (!isLightTheme.value)
    return {}

  return {
    'toc-text--read': currentChapterSequenceNo.value !== null && item.sequenceNo < currentChapterSequenceNo.value,
    'toc-text--default': props.chapterId !== item.id && (currentChapterSequenceNo.value === null || item.sequenceNo >= currentChapterSequenceNo.value),
  }
}

function getOptionTextClass(item: any) {
  const isCurrent = pagination.value.pageIndex === item.value
  if (isCurrent)
    return { 'toc-text--current': true }

  if (!isLightTheme.value)
    return {}

  return {
    'toc-text--default': pagination.value.pageIndex !== item.value,
  }
}

async function fetchChapterList() {
  $showLoading()
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', { params: { contentId: props.contentId, ...pagination.value } })
    chapterList.value = chapterListData.records
    chapterTotal.value = chapterListData.total
    const page = chapterListData.pages
    chapterOptions.value = Array.from({ length: page }).map((_, index) => ({
      label: `${(index * pagination.value.pageSize + 1)} - ${(index + 1) * pagination.value.pageSize}`,
      value: index + 1,
    }))
    if (props.chapterId) {
      const itemHeight = showUpdateTime.value ? 65 : 57
      const index = chapterList.value.findIndex(item => item.id === props.chapterId)
      nextTick(() => {
        if (index > -1)
          chapterScrollY.value = index * itemHeight
      })
    }
  }
  finally {
    $hideLoading()
  }
}
async function onOpen() {
  await fetchChapterList()
}

function selectChapter(item: any) {
  pagination.value.pageIndex = item.value
}

function toChapter($event: Event, item: any, navigate: any) {
  if (props.checkPassGotoChapter(item.sequenceNo))
    navigate($event)
}

watch(() => pagination.value.pageIndex, async () => {
  await fetchChapterList()
  chooseChapterOpitons.value = false
})

watch(
  () => props.value,
  (val) => {
    if (val)
      elementClick('catalogue')
  },
)
</script>

<template>
  <BasicPopup :value="value" @input="value => $emit('input', value)" @open="onOpen">
    <div
      v-if="book"
      class="max-h-90dvh overflow-scroll rounded-t toc-panel--flipnovel"
      :style="tocPanelStyle"
    >
      <div class="pt-5 pb-13px px-5 toc-header--flipnovel">
        <div class="flex space-x-10px">
          <img class="w-55px h-77px rounded-3px" :src="book.contentCoverUrl">
          <div class="flex flex-col justify-center">
            <p class="text-17px font-bold line-clamp-2" :class="{ 'toc-book-title--light': isLightTheme }">
              {{ book.contentName }}
            </p>
            <p class="mt-2 text-13px" :class="isLightTheme ? 'toc-book-count--light' : 'text-n-text-3'">
              {{ chapterTotal }} {{ t('episodes') }}
            </p>
          </div>
        </div>
      </div>
      <ul v-show="chooseChapterOpitons" class="px-5 h-[calc(90dvh-150px)] overflow-scroll">
        <li
          v-for="item in chapterOptions" :key="item.value"
          class="toc-item h-57px lh-57px flex items-center justify-between"
          @click="selectChapter(item)"
        >
          <span class="toc-text" :class="getOptionTextClass(item)">{{ item.label }}</span>
          <i-assets-icon-check v-if="pagination.pageIndex === item.value" class="w-17px" />
        </li>
      </ul>
      <ul v-show="!chooseChapterOpitons" ref="chapterScrollRef" class="px-5 h-[calc(90dvh-150px)] overflow-scroll">
        <router-link
          v-for="item in chapterList" :key="item.id"
          v-slot="{ navigate }" custom
          :to="{ name: routeName, params: { contentId, chapterId: item.id }, query: { skipPosition: '1' } }" replace
        >
          <li
            class="toc-item flex items-center justify-between"
            :class="showUpdateTime ? 'h-65px' : 'h-57px lh-57px'"
            @click="toChapter($event, item, navigate)"
          >
            <div v-if="showUpdateTime" class="flex-1 min-w-0">
              <div class="text-truncate" :class="getChapterTextClass(item)">
                {{ item.chapterName }}
              </div>
              <div class="mt-2 text-11px lh-12px toc-update-time">
                {{ formatUpdateTime(item.updateDateTime) }}
              </div>
            </div>
            <span v-else class="toc-text flex-1 text-truncate" :class="getChapterTextClass(item)">{{ item.chapterName }}</span>
            <i-assets-icon-lock
              v-if="item.lockStatus === CHAPTER_LOCK.LOCK"
              class="w-17px toc-lock-icon"
              :style="lockIconColor ? { color: lockIconColor } : undefined"
            />
          </li>
        </router-link>
      </ul>
    </div>
  </BasicPopup>
</template>

<style scoped>
.toc-panel--flipnovel {
  background: var(--flipnovel-toc-bg) !important;
}

.toc-header--flipnovel {
  background: var(--flipnovel-toc-bg) !important;
}

.toc-lock-icon {
  color: var(--toc-lock-color, rgba(0, 0, 0, 0.3));
}

.toc-item {
  border-bottom: 1px solid var(--toc-border-color, #ECEDEF);
}

.toc-text--read {
  color: #A8AFBB;
}

.toc-text--current {
  color: v-bind(tocCurrentColor);
  font-weight: 700;
}

.toc-text--default {
  color: #32353A;
}

.toc-book-title--light {
  color: #32353A;
}

.toc-book-count--light {
  color: #A8AFBB;
}

.toc-update-time {
  color: #A8AFBB;
}
</style>
